import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosInstance';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaFileImport, FaFileExport } from 'react-icons/fa';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import { hasPermission } from '../../utils/permissionHelper';

const GuestIndex = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const user = JSON.parse(localStorage.getItem("user")); // logged in user

  const getInitialLimit = () => {
    const stored = localStorage.getItem('guest_page_limit');
    return stored ? parseInt(stored) : 10;
  };
  const [limit, setLimit] = useState(getInitialLimit());

  useEffect(() => {
    localStorage.setItem('guest_page_limit', limit);
  }, [limit]);

  // Debounce search query - only trigger when 3+ characters or empty
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (hasPermission(user, "Guest", "Read")) {
      fetchUsers(currentPage, debouncedSearchQuery);
    }
  }, [currentPage, debouncedSearchQuery, limit]);

  const fetchUsers = async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams({
        userType: 'Guest',
        page,
        limit,
      });

      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const res = await axios.get(`/api/user?${params.toString()}`);
      const { users = [], totalCount = 0 } = res.data;
      setUsers(users);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      toastError('Failed to fetch users');
    }
  };

  // Use SweetAlert2 confirmDelete for the delete popup
  const handleDelete = async (id) => {
    const result = await confirmDelete("Do you want to delete this guest?");
    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/user/guests/${id}`);
        toastSuccess('User deleted successfully!');
        setCurrentPage(1);
        fetchUsers(1, debouncedSearchQuery);
      } catch (err) {
        toastError('Failed to delete user');
      }
    }
    // No need for else -- cancel just closes modal
  };

  const handleOpenImportModal = () => {
    setImportModalVisible(true);
    setImportFile(null);
  };
  const handleCloseImportModal = () => {
    setImportModalVisible(false);
    setImportFile(null);
  };
  const handleImportFileChange = (e) => {
    setImportFile(e.target.files[0]);
  };
  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toastError('Please select a file');
      return;
    }
    const formData = new FormData();
    formData.append('file', importFile);
    try {
      await axios.post('/api/user/import-excel', formData);
      toastSuccess('File imported successfully!');
      handleCloseImportModal();
      fetchUsers(currentPage, debouncedSearchQuery);
    } catch (err) {
      toastError('Import failed');
    }
  };
  const handleDownloadSample = () => {
    window.open('/public/assets/js/sample-user-import.xlsx', '_blank');
  };
  const handleExport = async () => {
    try {
      const params = {
        search: debouncedSearchQuery,
        userType: 'Guest',
        page: currentPage,
        limit,
      };
      const response = await axios.get('/api/user/export-excel', {
        params,
        responseType: 'blob',
      });
      const disposition = response.headers['content-disposition'];
      let filename = 'guests.xlsx';
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/"/g, '');
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toastSuccess('Export started');
    } catch (err) {
      toastError('Failed to export data');
    }
  };

  // 🔒 Check permission
  if (!hasPermission(user, "Guest", "Read")) {
    return (
      <div className="container py-4 text-center">
        <p className="text-danger fw-bold">
          🚫 You don’t have permission to view Guests.
        </p>
      </div>
    );
  }

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 text-dark">Guest Member</h3>
        <div className="d-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: 250 }}
            placeholder="Search Guest..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
          />
          <button
            className="btn btn-sm d-flex align-items-center gap-1 btn-success"
            type="button"
            onClick={handleExport}
          >
            <FaFileExport /> Export
          </button>
        </div>
      </div>

      {/* Import Modal */}
      {importModalVisible && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }} tabIndex="-1">
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleImportSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">Import Guests</h5>
                <button type="button" className="btn-close" onClick={handleCloseImportModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Select File</label>
                  <input type="file" accept=".csv,.xlsx" className="form-control" onChange={handleImportFileChange} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-info" onClick={handleDownloadSample}>Download Sample File</button>
                <button type="submit" className="btn btn-success">Import</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0 text-dark">All Guests</h5>
          {hasPermission(user, "Guest", "Create") && (
            <Link to="/guest/create" className="btn btn-sm btn-success text-white">
              + Add New Guest
            </Link>
          )}
        </div>
        <div className="table-responsive" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <div className="card-body p-0">
            <table className="table table-bordered table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th className="py-1">Sr no.</th>
                  <th className="py-1">First Name</th>
                  <th className="py-1">Middle Name</th>
                  <th className="py-1">Last Name</th>
                  <th className="py-1">Mobile Number</th>
                  <th className="py-1">Email</th>
                  <th className="py-1">Business Name</th>
                  <th className="py-1">Business Category</th>
                  <th className="py-1">Business Subcategory</th>
                  <th className="py-1 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((u, idx) => (
                    <tr key={u._id}>
                      <td>{(currentPage - 1) * limit + idx + 1}</td>
                      <td>
                {/*  <Link
                              to={`/guest/view/${u._id}`}
                              style={{ color: '#f94f01', textDecoration: 'underline', cursor: 'pointer' }}
                            > */}
                          {u.firstName || '-'}
                        {/*  </Link> */}
                      </td>
                      <td>{u.middleName || '-'}</td>
                      <td>{u.lastName || '-'}</td>
                      <td>{u.mobileNumber || '-'}</td>
                      <td>{u.email || '-'}</td>
                      <td>{u.business && u.business.length > 0 ? u.business[0].name || '-' : '-'}</td>
                      <td>{u.business && u.business.length > 0 ? (Array.isArray(u.business[0].category) ? u.business[0].category.map(cat => cat.name || cat).join(', ') : (u.business[0].category?.name || u.business[0].category || '-')) : '-'}</td>
                      <td>{u.business && u.business.length > 0 ? (Array.isArray(u.business[0].subCategory) ? u.business[0].subCategory.map(sc => sc.name || sc).join(', ') : (u.business[0].subCategory?.name || u.business[0].subCategory || '-')) : '-'}</td>
                      <td className="text-center">
                        {hasPermission(user, "Guest", "Edit") && (
                          <Link
                            to={`/guest/edit/${u._id}`}
                            className="btn btn-sm py-0 px-2 fs-9 lh-sm shadow-none border-0 text-primary-custom me-1"
                            title="Edit"
                          >
                            <FaEdit size={14} />
                          </Link>
                        )}
                        {hasPermission(user, "Guest", "Delete") && (
                          <button
                            className="btn btn-sm py-0 px-2 fs-9 lh-sm shadow-none border-0 text-danger"
                            onClick={() => handleDelete(u._id)}
                            title="Delete"
                          >
                            <FaTrash size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center">No guests found.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination & Limit */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 pt-2">
              <div>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={limit}
                  onChange={e => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>
              </div>
              <div className="flex-grow-1"></div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-end align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    &laquo;
                  </button>
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 3;
                    pages.push(
                      <button
                        key={1}
                        className={`btn btn-sm page-btn ${currentPage === 1 ? 'active' : ''}`}
                        onClick={() => setCurrentPage(1)}
                      >
                        1
                      </button>
                    );
                    if (currentPage > maxVisiblePages + 1) {
                      pages.push(
                        <span key="ellipsis1" className="px-2">...</span>
                      );
                    }
                    const startPage = Math.max(2, currentPage - 1);
                    const endPage = Math.min(totalPages - 1, currentPage + 1);
                    for (let page = startPage; page <= endPage; page++) {
                      if (page !== 1 && page !== totalPages) {
                        pages.push(
                          <button
                            key={page}
                            className={`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        );
                      }
                    }
                    if (currentPage < totalPages - maxVisiblePages) {
                      pages.push(
                        <span key="ellipsis2" className="px-2">...</span>
                      );
                    }
                    if (totalPages > 1) {
                      pages.push(
                        <button
                          key={totalPages}
                          className={`btn btn-sm page-btn ${currentPage === totalPages ? 'active' : ''}`}
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    return pages;
                  })()}
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    &raquo;
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestIndex;